import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./tekseris.css";

/** Утилита: күнді форматтау */
const fmt = (s) => (s ? new Date(s).toLocaleString("kk-KZ") : "—");

/** Утилита: статусқа сәйкес түстер */
const badgeClass = (status) => {
  if (!status) return "t-badge t-badge--gray";
  if (status === "signed") return "t-badge t-badge--green";
  if (status === "pending") return "t-badge t-badge--yellow";
  if (status === "rejected") return "t-badge t-badge--red";
  return "t-badge t-badge--blue";
};

export default function TekserisPage() {
  const [data, setData] = useState(null);
  const [openIds, setOpenIds] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  // 🔹 Бекенд сұранысы
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");

    if (!token) {
      setError("QR-токен табылмады");
      setLoading(false);
      return;
    }

    axios
      .get(`https://expertise.gov.kz/api/tekseris?token=${token}`)
      .then((res) => setData(res.data))
      .catch(() => setError("Деректерді алу кезінде қате орын алды"))
      .finally(() => setLoading(false));
  }, []);

  // 🔹 Деректер (әрдайым хук деңгейінде)
  const order = data?.order;
  const groups = data?.application_groups ?? [];
  const signers = data?.signers ?? [];

  // 🔹 useMemo — әрдайым шақырылсын (алдыңғы қателер осында болған)
  const signingStats = useMemo(() => {
    const total = signers.length;
    const done = signers.filter((s) => s.signed && !s.rejected).length;
    return { total, done };
  }, [signers]);

  // =======================
  //   РЕНДЕР БАСТАЛДЫ
  // =======================

  if (loading)
    return <div className="t-center">Жүктелуде...</div>;

  if (error)
    return (
      <div className="t-center text-red-600 font-semibold">
        {error}
      </div>
    );

  return (
    <div className="tekseris t-bg">
      <header className="t-header">
        <div className="t-header__left">
          <div className="t-header__brand">
            <div className="t-header__brand-title">
              expertise.gov.kz
            </div>
            <div className="t-header__brand-sub">
              Республикалық ғылыми-практикалық орталық БМСО
            </div>
          </div>
        </div>

        <div className="t-header__status">
          <span className={badgeClass(order?.status)}>
            {order?.status === "signed" ? "Қол қойылған" : order?.status || "—"}
          </span>
          <div className="t-progress">
            <div
              className="t-progress__bar"
              style={{
                width: `${(signingStats.done / Math.max(1, signingStats.total)) * 100}%`,
              }}
            />
          </div>
          <span className="t-progress__text">
            {signingStats.done}/{signingStats.total}
          </span>
        </div>
      </header>

      <main className="t-container">
        <div className="t-card">
          {/* 🔷 Бұйрық ақпараты */}
          <section className="t-section t-order">
            <div className="t-section__head">
              <h2 className="t-title">Бұйрық ақпараты</h2>
              <div className="t-section__right">
                <span className={badgeClass(order?.status)}>
                  {order?.status === "signed"
                    ? "Қол қойылған"
                    : order?.status || "—"}
                </span>
              </div>
            </div>

            <div className="t-grid t-grid--order">
              <div className="t-field">
                <div className="t-field__label">Нөмірі</div>
                <div className="t-field__value">
                  {order?.order_number || "—"}
                </div>
              </div>
              <div className="t-field">
                <div className="t-field__label">Құрылған уақыты</div>
                <div className="t-field__value">{fmt(order?.created_at)}</div>
              </div>
              <div className="t-field t-field--wide">
                <div className="t-field__label">Тақырып</div>
                <div className="t-field__value">{order?.title || "—"}</div>
                {order?.title2 && (
                  <div className="t-field__muted">{order.title2}</div>
                )}
                {order?.description && (
                  <div className="t-field__muted">{order.description}</div>
                )}
              </div>
            </div>
          </section>

          {/* 🔷 Екі баған: топтар + қол қоюшылар */}
          <div className="t-split">
            {/* Сол жақ: топтар */}
            <section className="t-section t-groups">
              <h3 className="t-subtitle">Өтініш топтары</h3>
              {groups.length === 0 && (
                <div className="t-muted">Тізім бос</div>
              )}

              <div className="t-accordions">
                {groups.map((g) => {
                  const isOpen = !!openIds[g.id];
                  return (
                    <div
                      key={g.id}
                      className={`t-acc ${isOpen ? "is-open" : ""}`}
                    >
                      <button
                        className="t-acc__head"
                        onClick={() =>
                          setOpenIds((p) => ({ ...p, [g.id]: !isOpen }))
                        }
                      >
                        <div className="t-acc__titles">
                          <div className="t-acc__title">{g.title}</div>
                          <div className="t-acc__sub">
                            {g.organization_name}
                          </div>
                        </div>
                        <span className="t-acc__chev" />
                      </button>
                      <div className="t-acc__body">
                        <ul className="t-list">
                          {(g.applications || []).map((a) => (
                            <li key={a.id} className="t-list__item">
                              <span className="t-dot" />
                              <span className="t-list__text">{a.title}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Оң жақ: қол қоюшылар */}
            <aside className="t-section t-signers">
              <h3 className="t-subtitle">Қол қоюшылар</h3>
              <ol className="t-timeline">
                {signers.map((s) => {
                  const state = s.rejected
                    ? "red"
                    : s.signed
                    ? "green"
                    : "gray";
                  return (
                    <li
                      key={s.user_id}
                      className={`t-timeline__item t-timeline__item--${state}`}
                    >
                      <div className="t-timeline__badge" />
                      <div className="t-signer">
                        <div className="t-signer__name">{s.full_name}</div>
                        <div className="t-signer__role">{s.role}</div>
                        <div className="t-signer__status">
                          {s.rejected ? (
                            <span className="t-text--red">❌ Бас тартылды</span>
                          ) : s.signed ? (
                            <span className="t-text--green">
                              ✅ Қол қойылды: {fmt(s.signed_at)}
                            </span>
                          ) : (
                            <span className="t-text--muted">⏳ Күтуде</span>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </aside>
          </div>
        </div>

        <footer className="t-footer">
          © {new Date().getFullYear()} Республикалық ғылыми-практикалық орталық · БМСО · expertise.gov.kz
        </footer>
      </main>
    </div>
  );
}